import { goRequest, goClient } from '../utils/httpClientTs';
import {
  RosterDuesListParams,
  RosterDuesListResponse,
  RosterDuesDetail,
  RosterDuesSummary,
  SPDRosterFormSummary,
  ValidateMemberRequest,
  ApprovalRequest,
  RejectionRequest,
  UpdateRosterFormRequest,
  GenericResponse,
  RosterDuesAccessResponse,
} from '../types/rosterDues';

export const getRosterDuesAccess =
  async (): Promise<RosterDuesAccessResponse> => {
    return goRequest<RosterDuesAccessResponse>({
      url: '/api/annual-reports/roster-and-dues/access',
      method: 'GET',
    });
  };

export const getRosterDuesList = async (
  params: RosterDuesListParams
): Promise<RosterDuesListResponse> => {
  const year = params.year ?? 2026;
  let url = `/api/annual-reports/roster-and-dues?page_size=${params.page_size}&page=${params.page}&year=${year}`;
  if (params.search) {
    url += `&search=${encodeURIComponent(params.search)}`;
  }
  if (params.order_by) {
    url += `&order_by=${params.order_by}`;
  }
  if (params.order) {
    url += `&order=${params.order}`;
  }
  if (params.spd) {
    url += `&spd=${encodeURIComponent(params.spd)}`;
  }
  if (params.assigned_to_me) {
    url += `&assigned_to_me=${params.assigned_to_me}`;
  }
  if (params.status_id) {
    url += `&status_id=${encodeURIComponent(params.status_id)}`;
  }
  if (params.form_type) {
    url += `&form_type=${params.form_type}`;
  }

  return goRequest<RosterDuesListResponse>({
    url: url,
    method: 'GET',
  });
};

// Local Chapter endpoints
export const getLocalChapterRosterDuesDetail = async (
  id: string
): Promise<RosterDuesDetail> => {
  return goRequest<RosterDuesDetail>({
    url: `/api/annual-reports/roster-and-dues/local-chapter/${id}`,
    method: 'GET',
  });
};

export const getLocalChapterRosterDuesSummary = async (
  id: string
): Promise<RosterDuesSummary> => {
  return goRequest<RosterDuesSummary>({
    url: `/api/annual-reports/roster-and-dues/local-chapter/${id}/summary`,
    method: 'GET',
  });
};

/** Fetches the Dues Form PDF for a local chapter. Returns the blob for download. */
export const getLocalChapterRosterDuesPDF = async (
  id: string
): Promise<Blob> => {
  const response = await goClient.get(
    `/api/annual-reports/roster-and-dues/local-chapter/${id}/pdf`,
    { responseType: 'blob' }
  );
  return response.data as Blob;
};

/** Fetches the Dues Form PDF for an SPD chapter. Returns the blob for download. */
export const getSPDRosterDuesPDF = async (id: string): Promise<Blob> => {
  const response = await goClient.get(
    `/api/annual-reports/roster-and-dues/spd/${id}/pdf`,
    { responseType: 'blob' }
  );
  return response.data as Blob;
};

// SPD endpoints
export const getSPDRosterDuesDetail = async (
  id: string
): Promise<RosterDuesDetail> => {
  return goRequest<RosterDuesDetail>({
    url: `/api/annual-reports/roster-and-dues/spd/${id}`,
    method: 'GET',
  });
};

export const getSPDRosterDuesSummary = async (
  id: string
): Promise<SPDRosterFormSummary> => {
  return goRequest<SPDRosterFormSummary>({
    url: `/api/annual-reports/roster-and-dues/spd/${id}/summary`,
    method: 'GET',
  });
};

// Legacy endpoints (deprecated - kept for backward compatibility if needed)
export const getRosterDuesDetail = async (
  id: string
): Promise<RosterDuesDetail> => {
  return goRequest<RosterDuesDetail>({
    url: `/api/annual-reports/roster-and-dues/${id}`,
    method: 'GET',
  });
};

export const getRosterDuesSummary = async (
  id: string
): Promise<RosterDuesSummary | SPDRosterFormSummary> => {
  return goRequest<RosterDuesSummary | SPDRosterFormSummary>({
    url: `/api/annual-reports/roster-and-dues/${id}/summary`,
    method: 'GET',
  });
};

export const validateMember = async (
  id: string,
  data: ValidateMemberRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/api/annual-reports/roster-and-dues/${id}/validate-member`,
    method: 'POST',
    data: data,
  });
};

export const updateMemberValidation = async (
  id: string,
  data: ValidateMemberRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/api/annual-reports/roster-and-dues/${id}/validate-member`,
    method: 'PUT',
    data: data,
  });
};

export const approveRosterDuesForm = async (
  id: string,
  data: ApprovalRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/api/annual-reports/roster-and-dues/${id}/approval`,
    method: 'POST',
    data: data,
  });
};

export const rejectRosterDuesForm = async (
  id: string,
  data: RejectionRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/api/annual-reports/roster-and-dues/${id}/rejection`,
    method: 'POST',
    data: data,
  });
};

export const updateLocalChapterRosterForm = async (
  id: string,
  data: UpdateRosterFormRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/api/annual-reports/roster-and-dues/local-chapter/${id}`,
    method: 'PATCH',
    data: data,
  });
};

export const updateSPDRosterForm = async (
  id: string,
  data: UpdateRosterFormRequest
): Promise<GenericResponse> => {
  return goRequest<GenericResponse>({
    url: `/api/annual-reports/roster-and-dues/spd/${id}`,
    method: 'PATCH',
    data: data,
  });
};
